import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import subscriptionService, { DEFAULT_PLANS, HEALTHCARE_MODULES } from 'services/subscriptionService';
import { getSocket } from 'services/socket';

// ==============================|| SUBSCRIPTION CONTEXT ||============================== //

export const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('yearly'); // All subscription plans are yearly

  // Modals & Drawers
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState(null);
  const [checkoutDrawerOpen, setCheckoutDrawerOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [lockedModal, setLockedModal] = useState({ open: false, module: null });

  // Load plans & current status
  const fetchSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, currentRes] = await Promise.all([
        subscriptionService.getPlans(),
        subscriptionService.getCurrentSubscription()
      ]);

      if (plansRes?.data) setPlans(plansRes.data);
      if (currentRes?.data) setCurrentSubscription(currentRes.data);
    } catch (err) {
      console.error('Failed to load subscription context:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionData();

    // Real-time synchronization via WebSocket (no page reload required)
    const socket = getSocket();
    if (socket) {
      const handleSubscriptionUpdated = (data) => {
        console.log('🔄 [SubscriptionContext] WebSocket SUBSCRIPTION_UPDATED received:', data);
        fetchSubscriptionData();
      };

      socket.on('SUBSCRIPTION_UPDATED', handleSubscriptionUpdated);
      return () => {
        socket.off('SUBSCRIPTION_UPDATED', handleSubscriptionUpdated);
      };
    }
  }, [fetchSubscriptionData]);

  // Derived calculations
  const hasActiveSubscription = useMemo(() => {
    return currentSubscription?.status === 'active' || currentSubscription?.status === 'expiring_soon';
  }, [currentSubscription]);

  const isExpired = useMemo(() => {
    return currentSubscription?.status === 'expired';
  }, [currentSubscription]);

  const isExpiringSoon = useMemo(() => {
    if (currentSubscription?.status === 'expiring_soon') return true;
    if (!currentSubscription?.expiry_date) return false;
    const diff = new Date(currentSubscription.expiry_date).getTime() - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 && days <= 7;
  }, [currentSubscription]);

  const daysRemaining = useMemo(() => {
    if (!currentSubscription?.expiry_date) return 0;
    const diff = new Date(currentSubscription.expiry_date).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [currentSubscription]);

  // Check if a specific module is unlocked
  const isModuleUnlocked = useCallback(
    (moduleOrItem) => {
      if (!moduleOrItem) return true;

      let rawString = '';
      if (typeof moduleOrItem === 'object') {
        rawString = `${moduleOrItem.url || ''} ${moduleOrItem.route || ''} ${moduleOrItem.link || ''} ${moduleOrItem.code || ''} ${moduleOrItem.title || ''} ${moduleOrItem.name || ''} ${moduleOrItem.id || ''}`;
      } else {
        rawString = String(moduleOrItem);
      }
      const normKey = rawString.toLowerCase().replace(/[\s-]+/g, '_');

      // 1. Super Admin always has full access
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || 'null');
        const role = String(storedUser?.role_name || storedUser?.role?.role_name || storedUser?.role || '').toLowerCase();
        if (role === 'super_admin' || role === 'super admin' || role === 'admin') {
          return true;
        }
      } catch {}

      // 2. Free core system pages are always accessible (never locked)
      const freeModules = ['dashboard', 'default', 'profile', 'clinic_profile', 'settings', 'support', 'subscription', 'notification', 'user', 'access_management', 'role', 'permission'];
      if (freeModules.some((fm) => normKey.includes(fm))) return true;

      // 3. Normalize key into standard module category
      let targetModule = normKey;
      if (normKey.includes('doctor')) targetModule = 'doctors';
      else if (normKey.includes('appointment') || normKey.includes('queue') || normKey.includes('calendar')) targetModule = 'appointments';
      else if (normKey.includes('patient')) targetModule = 'patients';
      else if (normKey.includes('bill') || normKey.includes('invoice') || normKey.includes('expense') || normKey.includes('transaction')) targetModule = 'billing';
      else if (normKey.includes('pharmacy')) targetModule = 'pharmacy';
      else if (normKey.includes('external_lab_work') || normKey.includes('external_lab')) targetModule = 'external_lab_work';
      else if (normKey.includes('lab') || normKey.includes('test')) targetModule = 'lab_requests';
      else if (normKey.includes('prescription')) targetModule = 'prescriptions';
      else if (normKey.includes('medical_record') || normKey.includes('record')) targetModule = 'clinical_records';
      else if (normKey.includes('clinical')) targetModule = 'clinical_management';
      else if (normKey.includes('report') || normKey.includes('analytics')) targetModule = 'reports';
      else if (normKey.includes('inventory') || normKey.includes('stock')) targetModule = 'inventory';

      // 4. If status is pending_review, core modules are waiting for activation
      if (currentSubscription?.status === 'pending_review' || currentSubscription?.request_status === 'pending_review') {
        return false;
      }

      // 5. If subscription is expired, inactive or cancelled
      if (isExpired || currentSubscription?.status === 'none' || currentSubscription?.status === 'cancelled') {
        return false;
      }

      // 6. Check against unlocked_modules list (defaults to core Starter modules)
      const unlocked = currentSubscription?.unlocked_modules || ['doctors', 'appointments', 'patients'];
      if (!Array.isArray(unlocked)) return false;

      // Direct match
      if (unlocked.includes(targetModule)) return true;

      // Clinical Management parent unlocks all clinical sub-modules
      const clinicalSubmodules = ['clinical_management', 'clinical_records', 'prescriptions', 'lab_requests', 'external_lab_work'];
      if (clinicalSubmodules.includes(targetModule)) {
        if (unlocked.includes('clinical_management') || unlocked.includes('clinical')) return true;
      }

      // Billing parent unlocks all billing sub-modules
      const billingSubmodules = ['billing', 'invoices', 'expenses', 'transactions'];
      if (billingSubmodules.includes(targetModule)) {
        if (unlocked.includes('billing')) return true;
      }

      // Reports parent unlocks all reports sub-modules
      const reportSubmodules = ['reports', 'business_analytics', 'analytics'];
      if (reportSubmodules.includes(targetModule)) {
        if (unlocked.includes('reports') || unlocked.includes('analytics')) return true;
      }

      // Pharmacy parent unlocks pharmacy sub-modules
      const pharmacySubmodules = ['pharmacy', 'pharmacy_billing'];
      if (pharmacySubmodules.includes(targetModule)) {
        if (unlocked.includes('pharmacy')) return true;
      }

      return false;
    },
    [isExpired, currentSubscription]
  );

  // Helper to open checkout review drawer
  const openCheckout = useCallback(
    (plan, cycle = billingCycle) => {
      setSelectedPlanForCheckout(plan);
      setBillingCycle(cycle);
      setCheckoutDrawerOpen(true);
    },
    [billingCycle]
  );

  const closeCheckout = useCallback(() => {
    setCheckoutDrawerOpen(false);
  }, []);

  // Helper to prompt locked module modal
  const openLockedModal = useCallback((moduleOrItem) => {
    let moduleInfo = moduleOrItem;
    if (typeof moduleOrItem === 'string') {
      moduleInfo = HEALTHCARE_MODULES.find((m) => m.code === moduleOrItem) || {
        code: moduleOrItem,
        name: moduleOrItem,
        minimumTier: 'professional'
      };
    }
    setLockedModal({ open: true, module: moduleInfo });
  }, []);

  const closeLockedModal = useCallback(() => {
    setLockedModal({ open: false, module: null });
  }, []);

  // Quick state simulation for live testing / demo review
  const switchSimulationState = useCallback((status, tier = 'professional') => {
    const updated = subscriptionService.simulateState(status, tier);
    setCurrentSubscription(updated);
  }, []);

  const enableAutopay = useCallback(async (payload = {}) => {
    const res = await subscriptionService.enableAutopay(payload);
    await fetchSubscriptionData();
    return res;
  }, [fetchSubscriptionData]);

  const pauseAutopay = useCallback(async (payload = {}) => {
    const res = await subscriptionService.pauseAutopay(payload);
    await fetchSubscriptionData();
    return res;
  }, [fetchSubscriptionData]);

  const resumeAutopay = useCallback(async (payload = {}) => {
    const res = await subscriptionService.resumeAutopay(payload);
    await fetchSubscriptionData();
    return res;
  }, [fetchSubscriptionData]);

  const value = useMemo(
    () => ({
      plans,
      currentSubscription,
      loading,
      billingCycle,
      setBillingCycle,
      hasActiveSubscription,
      isExpired,
      isExpiringSoon,
      daysRemaining,
      isModuleUnlocked,
      selectedPlanForCheckout,
      checkoutDrawerOpen,
      openCheckout,
      closeCheckout,
      upgradeModalOpen,
      setUpgradeModalOpen,
      lockedModal,
      openLockedModal,
      closeLockedModal,
      switchSimulationState,
      enableAutopay,
      pauseAutopay,
      resumeAutopay,
      autopayStatus: currentSubscription?.autopay_status || 'disabled',
      refreshSubscription: fetchSubscriptionData
    }),
    [
      plans,
      currentSubscription,
      loading,
      billingCycle,
      hasActiveSubscription,
      isExpired,
      isExpiringSoon,
      daysRemaining,
      isModuleUnlocked,
      selectedPlanForCheckout,
      checkoutDrawerOpen,
      openCheckout,
      closeCheckout,
      upgradeModalOpen,
      lockedModal,
      openLockedModal,
      closeLockedModal,
      switchSimulationState,
      enableAutopay,
      pauseAutopay,
      resumeAutopay,
      fetchSubscriptionData
    ]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

SubscriptionProvider.propTypes = {
  children: PropTypes.node
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
